import { useState, useEffect } from 'react';
import api from '../utils/api';
import { logger } from '../utils/logger';
import { TemplateSettings } from '../components/templates/types';

/**
 * @deprecated Use TemplateSettings from '../components/templates/types' instead
 * Kept for backward compatibility
 */
export type DocumentTemplate = TemplateSettings;

export interface CompanyTemplates {
  invoiceTemplateId: string | null;
  quoteTemplateId: string | null;
  invoiceTemplate: TemplateSettings | null;
  quoteTemplate: TemplateSettings | null;
}

/**
 * Hook pour charger les templates résolus de l'entreprise
 * Utilise le nouveau système de résolution de templates basé sur CompanyTemplatePreference
 */
export function useTemplateSettings() {
  const [templates, setTemplates] = useState<CompanyTemplates | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger les deux templates en parallèle via le resolver
      const [invoiceResp, quoteResp] = await Promise.all([
        api.get('/templates/resolve/INVOICE').catch(err => {
          logger.error('Erreur résolution template facture:', err);
          return null;
        }),
        api.get('/templates/resolve/QUOTE').catch(err => {
          logger.error('Erreur résolution template devis:', err);
          return null;
        }),
      ]);

      // API returns data directly or wrapped in { data: ... }
      const invoiceTemplate = (invoiceResp as any)?.data ?? invoiceResp ?? null;
      const quoteTemplate = (quoteResp as any)?.data ?? quoteResp ?? null;

      setTemplates({
        invoiceTemplateId: invoiceTemplate?.id || null,
        quoteTemplateId: quoteTemplate?.id || null,
        invoiceTemplate,
        quoteTemplate,
      });

      logger.log('Templates résolus:', {
        invoice: invoiceTemplate?.name,
        quote: quoteTemplate?.name,
      });
    } catch (err: any) {
      logger.error('Erreur chargement templates:', err);
      setError(err.response?.data?.error || 'Erreur de chargement des templates');
    } finally {
      setLoading(false);
    }
  };

  return {
    templates,
    loading,
    error,
    reload: loadTemplates,
  };
}

/**
 * Hook pour obtenir uniquement le template de facture
 */
export function useInvoiceTemplate() {
  const { templates, loading, error } = useTemplateSettings();
  return {
    template: templates?.invoiceTemplate,
    loading,
    error,
  };
}

/**
 * Hook pour obtenir uniquement le template de devis
 */
export function useQuoteTemplate() {
  const { templates, loading, error } = useTemplateSettings();
  return {
    template: templates?.quoteTemplate,
    loading,
    error,
  };
}
